import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {UnmountClosed} from 'react-collapse';
import {Checkbox, Space} from 'antd';
import {useDispatch} from 'react-redux';
import {
  StyledInvoicePaymentDetails,
  StyledInvoicePaymentDetailsReasonTitle
} from './StyledInvoicePaymentDetails';
import {StyledUserReviewCollapseIcon} from '../../../UserReview/StyledUserReview';
import RejectExpenseModal
  from '../../ExpensesPage/ExpenseDetailsPanelTabs/tabComponents/RejectExpenseModal/RejectExpenseModal';
import ApprovalsTab from '../../ExpensesPage/ExpenseDetailsPanelTabs/ApprovalsTab';
import {ChevronDownIcon} from '../../../../icons';
import {DEFAULT_MODAL_PROPS, invoiceApprovalStatusConstants} from '../../../../constants';
import {invoicesActions} from '../../../../state/actions/invoice.actions';
import {alertActions} from '../../../../state/actions/alert.actions';


const InvoicePaymentDetails = ({
  approvals = [],
  invoice,
  onUpdate,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'invoices']);
  const [showComment, setShowComment] = useState(true);
  const [rejectModalProps, setRejectModalProps] = useState(DEFAULT_MODAL_PROPS);
  const [isPaymentExternal, setIsPaymentExternal] = useState(!!invoice?.paid_outside);

  const dispatch = useDispatch();
  const invoiceId = invoice?.id;

  const isApprovedInvoice = invoice?.approval_status === invoiceApprovalStatusConstants.APPROVED;

  const comment = invoice?.payment_request_reason;

  const handleReassign = (e, data) => {
    dispatch(invoicesActions.reassignInvoice({
      id: invoiceId,
      data,
      successCallback: (response) => onUpdate?.(invoice, {approvals: response.results || []}),
      errorCallback: () => alertActions.error(t('invoices:alert.errorReassignInvoiceMessage')),
    }))
  }

  const handleApprove = ({data, id}) => {
    if (!invoiceId || !id) return;

    dispatch(invoicesActions.approveInvoiceApproval({
      id: invoiceId,
      approvalId: id,
      data,
      successCallback: (response) => onUpdate?.(null, {approvals: response.results || []}),
      errorCallback: () => alertActions.error(t('invoices:alert.errorApproveInvoiceMessage')),
    }));
  }

  const handleReject = ({id} = {}) => setRejectModalProps({...rejectModalProps, open: true, approvalId: id});

  const handleOkRejectModal = ({fields, successCallback, errorCallback}) => {
    if (!invoiceId) return;

    setRejectModalProps(prevState => ({...prevState, loading: true}));

    dispatch(invoicesActions.rejectInvoiceApproval({
      id: invoiceId,
      approvalId: rejectModalProps.approvalId,
      data: fields,
      successCallback: (response) => {
        handleCloseRejectModal();
        onUpdate?.(null, {approvals: response.results || []});
        successCallback?.(response);
      },
      errorCallback: (err) => {
        setRejectModalProps(prevState => ({...prevState, loading: false}));
        errorCallback?.(err?.response);
      },
    }))
  }

  const handleReviewEdit = ({id} = {}) => {
    if (!invoiceId || !id) return;

    dispatch(invoicesActions.reopenInvoiceApproval({
      id: invoiceId,
      approvalId: id,
      successCallback: (response) => onUpdate?.(null, {approvals: response.results || []}),
      errorCallback: () => alertActions.error(t('invoices:alert.errorReopenInvoiceMessage')),
    }))
  }

  const handleCloseRejectModal = () => setRejectModalProps(DEFAULT_MODAL_PROPS);

  const handlePaidOutside = ({paid = true, successCallback} = {}) => {
    if (!invoice) return;

    dispatch(invoicesActions.payInvoiceOutside({
      id: invoice.id,
      paid,
      successCallback,
      errorCallback: (resp) => {
        alertActions.error(resp?.response?.data?.message || t('invoices:alert.errorPayInvoiceOutsideMessage'));
        setIsPaymentExternal(!paid);
      }
    }));
  }

  const handleSelectPaymentExternal = (e) => {
    const paidOutside = e.target.checked;
    setIsPaymentExternal(paidOutside);
    handlePaidOutside({paid: paidOutside});
  }

  return (
    <StyledInvoicePaymentDetails {...rest}>
      <Space size='large' direction='vertical'>
        <Space size={4} direction='vertical'>
          <Space
            align='center'
            className='d-flex-child pointer'
            onClick={() => setShowComment(prev => !prev)}
            size='small'
          >
            <StyledUserReviewCollapseIcon rotate={`${showComment ? '-180deg' : '0deg'}`} size={16}>
              <ChevronDownIcon />
            </StyledUserReviewCollapseIcon>
            <StyledInvoicePaymentDetailsReasonTitle>
              {t('reason')}
            </StyledInvoicePaymentDetailsReasonTitle>
          </Space>
          <UnmountClosed isOpened={showComment}>
            <span>{comment}</span>
          </UnmountClosed>
        </Space>
        <ApprovalsTab
          onApprove={handleApprove}
          onReassign={handleReassign}
          onReject={handleReject}
          onReviewEdit={handleReviewEdit}
          className='approvals-tab'
          entity={{approval: approvals}}
          inReviewModal={invoice?.appoval_status === invoiceApprovalStatusConstants.PENDING}
        />

        {isApprovedInvoice && (
          <Checkbox
            checked={isPaymentExternal}
            onChange={handleSelectPaymentExternal}
          >
            {t('invoices:paidOutsideWithLess')}
          </Checkbox>
        )}
      </Space>

      <RejectExpenseModal
        {...rejectModalProps}
        handleCancel={handleCloseRejectModal}
        handleOk={handleOkRejectModal}
      />

    </StyledInvoicePaymentDetails>
  );
}

InvoicePaymentDetails.propTypes = {
  approvals: PropTypes.arrayOf(
    PropTypes.shape({
      approval_date: PropTypes.string,
      id: PropTypes.string,
      is_approved: PropTypes.bool,
      name: PropTypes.string,
    })
  ),
  invoice: PropTypes.shape({

  }),
  onUpdate: PropTypes.func,
}

export default InvoicePaymentDetails;
